import * as PIXI from "pixi.js";
import { FONT_STYLES, scaleStyle } from "../fontStyles";
import { getTapWordAnchor } from "./tapWordAnchor";
import {
  TAP_TO_CONTINUE_FONT_SIZE,
  TAP_TO_CONTINUE_HIT_HEIGHT,
} from "./attachTapToContinue";

/**
 * The tap-to-continue word for a popup built from plain PIXI containers (no
 * spine) — the same rules as attachTapToContinue:
 *
 *   - size 42 (scaleStyle), centred
 *   - on the intro popup's tap line and size (utils/tapWordAnchor), solved
 *     into the holder's own units; without an anchor it sits on the holder
 *   - never wider than `width` (holder units) or 80% of the screen
 *   - hit area `width` x 80 around the word
 *
 * @param {Object}    p
 * @param {PIXI.Container} p.holder  Where the word goes; its position is the
 *                                   word's resting place (the "bone").
 * @param {number}    p.width        Room the word has, holder units.
 * @param {string}    p.label        Already localised and upper-cased.
 * @param {Object}   [p.style]       fontStyles entry; tapToContinue by default.
 * @param {Function} [p.onClick]
 * @param {Function} [p.onFit]    After every placement (the word may have moved).
 * @param {string}   [p.name]
 * @returns {{container: PIXI.Container, text: PIXI.Text, fit: Function,
 *            setOffsetY: Function}}
 */
export function placeTapToContinue({
  holder,
  width,
  label,
  style = FONT_STYLES.tapToContinue,
  onClick = null,
  onFit = null,
  name = "tap_to_continue",
}) {
  const container = new PIXI.Container();
  container.name = name;
  const text = new PIXI.Text(
    label,
    new PIXI.TextStyle(scaleStyle(style, TAP_TO_CONTINUE_FONT_SIZE)),
  );
  text.resolution = 2;
  text.anchor.set(0.5);
  container.addChild(text);
  holder.addChild(container);

  let manualOffsetY = 0;
  let refitRAF = null;

  /** The holder's screen y and scale, or null before it is on a stage. */
  const holderWorld = () => {
    try {
      const { y } = holder.getGlobalPosition(undefined, false);
      const scale = holder.worldTransform.d;
      return Number.isFinite(y) && scale ? { y, scale } : null;
    } catch {
      return null;
    }
  };

  // A resize reaches fit() before PIXI has applied the new parent transforms,
  // so it fits once more on the next frame (one pending pass at most).
  const scheduleRefit = (dimensions, deferred) => {
    if (deferred || typeof requestAnimationFrame !== "function") return;
    if (refitRAF !== null) cancelAnimationFrame(refitRAF);
    refitRAF = requestAnimationFrame(() => {
      refitRAF = null;
      fit(dimensions, { deferred: true });
    });
  };

  const fit = (dimensions, { deferred = false } = {}) => {
    if (!container.transform || !holder.transform) return;
    const anchor = getTapWordAnchor();
    const world = holderWorld();
    if (anchor && !world) {
      scheduleRefit(dimensions, deferred);
      return;
    }

    text.scale.set(1);
    const naturalWidth = text.width;
    let scale = 1;
    if (anchor) {
      scale = anchor.fontSize / (TAP_TO_CONTINUE_FONT_SIZE * world.scale);
      container.y = (anchor.y - world.y) / world.scale;
    } else {
      container.y = manualOffsetY;
    }

    const screenLocal = ((dimensions?.width || 800) * 0.8) / (world?.scale || 1);
    // const maxWidth = Math.min(width, screenLocal);
    // The screen only (Loc QA, 2026-10-28): `width` squeezed the long
    // translations (RU / FR-CA / FI) below the start page's size; the word
    // floats free under the card, on no plate.
    const maxWidth = screenLocal;
    if (naturalWidth * scale > maxWidth) scale = maxWidth / naturalWidth;
    text.scale.set(scale);
    onFit?.();
    scheduleRefit(dimensions, deferred);
  };

  /** Nudge off the holder, holder units; ignored while the anchor places it. */
  const setOffsetY = (y) => {
    manualOffsetY = y || 0;
    if (!getTapWordAnchor()) {
      container.y = manualOffsetY;
      onFit?.();
    }
  };

  if (onClick) {
    container.eventMode = "static";
    container.cursor = "pointer";
    container.hitArea = new PIXI.Rectangle(
      -width / 2,
      -TAP_TO_CONTINUE_HIT_HEIGHT / 2,
      width,
      TAP_TO_CONTINUE_HIT_HEIGHT,
    );
    container.on("pointerdown", (evt) => {
      evt?.stopPropagation?.();
      onClick(evt);
    });
  }

  fit(null);
  return { container, text, fit, setOffsetY };
}

export default placeTapToContinue;
